<template>
  <div class="assinador-documento">
    <div
      v-if="error"
      class="alert alert-danger"
    >
      {{ error }}
    </div>
    <div
      v-if="!error"
      class="pdf-container"
    >
      <div class="viewer-header">
        <h4>Visualizar e Assinar Documento</h4>
        <small v-if="propostaNumero">Proposta: {{ propostaNumero }}</small>
      </div>

      <!-- Display user information -->
      <div
        v-if="assinanteInfo"
        class="assinante-info"
      >
        <h5>Informações do Assinante / Documento</h5>
        <p><strong>Arquivo:</strong> {{ assinanteInfo.nomeArquivo }}</p>
        <p><strong>E-mail do destinatário:</strong> {{ assinanteInfo.email }}</p>
        <p class="small text-muted">
          O assinante deverá preencher seus dados pessoais ao assinar.
        </p>

        <!-- Checklist simples -->
        <ul class="small">
          <li>1. Visualize o documento</li>
          <li>2. Confirme que leu</li>
          <li>3. Preencha seus dados pessoais</li>
          <li>4. Assine</li>
        </ul>
      </div>

      <div
        ref="viewer"
        class="viewer"
      >
        <!-- Embed PDF using iframe like VisualizarArquivo -->
        <div
          v-if="!arquivoUrl && !isLoading"
          class="loading-pdf"
        >
          Carregando PDF...
        </div>

        <iframe
          v-if="arquivoUrl"
          :src="pdfUrl"
          frameborder="0"
          height="100%"
          width="100%"
        />
        <visualizar-arquivo
          v-else-if="arquivoUrl"
          :url="arquivoUrl"
        />
        <div
          v-else
          class="no-file"
        >
          Arquivo não disponível
        </div>
      </div>

      <div class="actions">
        <b-form-checkbox
          v-model="confirmRead"
          class="mb-2"
        >
          Confirmo que li o documento e estou de acordo com os termos.
        </b-form-checkbox>

        <b-button
          :disabled="!confirmRead"
          variant="primary"
          @click="openSignModal"
        >
          Assinar Documento
        </b-button>
      </div>
    </div>

    <!-- Sign modal -->
    <b-modal
      id="sign-modal"
      ref="signModal"
      title="Assinar documento"
      hide-footer
    >
      <div>
        <p>Para assinar, confirme seus dados abaixo.</p>
        <b-form
          @submit.prevent="submitSignature"
        >
          <b-form-group
            label="Nome completo"
            label-for="nome"
          >
            <b-form-input
              id="nome"
              v-model="form.nome"
              required
            />
          </b-form-group>

          <!-- Tipo de inscrição -->
          <b-form-group
            label="Tipo de inscrição"
            label-for="tipo-inscricao"
          >
            <b-form-select
              id="tipo-inscricao"
              v-model="form.tipoInscricao"
              :options="[{ value: 'CPF', text: 'CPF' }, { value: 'CNPJ', text: 'CNPJ' }]"
              required
            />
          </b-form-group>

          <!-- Inscrição (CPF/CNPJ) -->
          <b-form-group
            label="Inscrição"
            label-for="inscricao"
          >
            <b-form-input
              id="inscricao"
              v-model="form.inscricao"
              :placeholder="inscricaoPlaceholder"
              :maxlength="inscricaoMaxlengthDigits"
              inputmode="numeric"
              required
              @input="onInscricaoInput"
              @blur="onInscricaoBlur"
            />
            <small class="text-muted">Informe o CPF ou CNPJ conforme selecionado.</small>
          </b-form-group>

          <b-form-group
            label="Data de nascimento"
            label-for="data-nasc"
          >
            <b-form-input
              id="data-nasc"
              v-model="form.dataNascimento"
              type="date"
              required
            />
          </b-form-group>

          <div class="text-right">
            <b-button
              variant="secondary"
              @click="$refs.signModal.hide()"
            >
              Cancelar
            </b-button>

            <b-button
              class="ml-2"
              variant="primary"
              type="submit"
            >
              Confirmar e Assinar
            </b-button>
          </div>
        </b-form>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import axios from '@axios'
// import api from './shared/services/api'
import docuSign from '@/shared/mixins/docu-sign/docu-sign'
import VisualizarArquivo from '@/shared/components/visualizar-arquivo/VisualizarArquivo.vue'

export default {
  name: 'AssinadorDocumento',
  components: {
    VisualizarArquivo,
  },
  data() {
    return {
      error: null,
      arquivoUrl: null,
      confirmRead: false,
      token: null,
      assinanteInfo: null,
      chavePublicaDocumento: null,
      form: {
        nome: '',
        tipoInscricao: 'CPF', // CPF | CNPJ
        inscricao: '',
        dataNascimento: '', // ISO yyyy-mm-dd
      },
      collected: {
        geolocation: null,
        device: null,
        ip: null,
        timestamps: {},
      },
      propostaNumero: null,
    }
  },
  computed: {
    pdfUrl() {
      if (!this.arquivoUrl) {
        return ''
      }
      return `https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(this.arquivoUrl)}&embedded=true`
    },
    inscricaoPlaceholder() {
      return this.form.tipoInscricao === 'CNPJ' ? '00.000.000/0000-00' : '000.000.000-00'
    },
    // maxlength usado enquanto digita (apenas dígitos)
    inscricaoMaxlengthDigits() {
      return this.form.tipoInscricao === 'CNPJ' ? 14 : 11
    },
    ...mapState({}),
  },
  watch: {
    'form.tipoInscricao': function formTipoInscricao() {
      // reformata o que já foi digitado ao trocar tipo
      this.form.inscricao = this.formatInscricao(this.form.inscricao || '')
    },
  },
  created() {
    this.init()
  },
  methods: {
    onInscricaoInput(e) {
      // mantém apenas dígitos enquanto digita (a formatação completa será aplicada no blur)
      const raw = (e.target.value || '')
      this.form.inscricao = String(raw).replace(/\D/g, '').slice(0, this.inscricaoMaxlengthDigits)
    },
    onInscricaoBlur(e) {
      // ao perder o foco, aplica máscara/formatação ao valor atual
      this.form.inscricao = this.formatInscricao(this.form.inscricao || (e && e.target ? e.target.value : ''))
    },
    formatInscricao(value) {
      const digits = String(value).replace(/\D/g, '')
      if (this.form.tipoInscricao === 'CNPJ') {
        const d = digits.slice(0, 14)
        // 00.000.000/0000-00
        return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2}).*/, (m, a, b, c, d2, e2) => `${a}${b ? `.${b}` : ''}${c ? `.${c}` : ''}${d2 ? `/${d2}` : ''}${e2 ? `-${e2}` : ''}`)
      } if (this.form.tipoInscricao === 'CPF') {
        // default CPF
        const d = digits.slice(0, 11)
        // 000.000.000-00
        return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{0,2}).*/, (m, a, b, c, d2) => `${a}${b ? `.${b}` : ''}${c ? `.${c}` : ''}${d2 ? `-${d2}` : ''}`)
      }
      // If no tipoInscricao selected, return the original value unformatted
      return String(value || '')
    },
    init() {
      this.$isLoading(true)
      const documentoId = this.$route && this.$route.params ? this.$route.params.documentoId : null
      const token = this.$route && this.$route.query ? this.$route.query.token : null
      const tenantIdentifier = this.$route && this.$route.query ? this.$route.query.tenant : null
      this.propostaNumero = this.$route && this.$route.query ? this.$route.query.numeroProposta : null
      if (!documentoId) {
        this.error = 'Documento não informado.'
        this.$isLoading(false)
        return
      }

      this.token = token

      docuSign.getDadosAssinante(documentoId, token, tenantIdentifier)
        .then(resp => {
          // backend agora retorna: nomeArquivo, urlArquivo e email
          const d = resp.data || {}
          this.assinanteInfo = {
            nomeArquivo: d.nomeArquivo,
            email: d.email,
          }
          this.arquivoUrl = d.urlArquivo
          this.chavePublicaDocumento = d.chavePublicaDocumento
          // não pré-preencher nome/inscrição: assinante preencherá ao assinar
          this.form.nome = ''
          this.form.dataNascimento = ''
        })
        .catch(() => {
          this.error = 'Erro ao carregar o documento. Tente novamente mais tarde.'
        })
        .finally(() => {
          this.$isLoading(false)
          this.collectDeviceInfo()
          this.collectIP()
        })
    },

    openSignModal() {
      this.requestGeolocation()
      this.$refs.signModal.show()
    },

    requestGeolocation() {
      if (!navigator.geolocation) {
        this.collected.geolocation = { error: 'Geolocation not supported' }
        return
      }
      const startedAt = performance.now()
      this.collected.timestamps.geoRequested = moment().toISOString()
      navigator.geolocation.getCurrentPosition(position => {
        const gotAt = performance.now()
        this.collected.geolocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          altitude: position.coords.altitude,
          altitudeAccuracy: position.coords.altitudeAccuracy,
          heading: position.coords.heading,
          speed: position.coords.speed,
        }
        this.collected.timestamps.geoAcquired = moment().toISOString()
        this.collected.timestamps.geoMs = Math.round(gotAt - startedAt)
      }, err => {
        this.collected.geolocation = { error: err.message }
        this.collected.timestamps.geoAcquired = moment().toISOString()
      }, { enableHighAccuracy: true, timeout: 10000 })
    },

    collectDeviceInfo() {
      try {
        const nav = window.navigator
        const device = {
          userAgent: nav.userAgent,
          platform: nav.platform,
          language: nav.language,
          languages: nav.languages,
          cookieEnabled: nav.cookieEnabled,
          screen: {
            width: window.screen.width,
            height: window.screen.height,
            availWidth: window.screen.availWidth,
            availHeight: window.screen.availHeight,
            colorDepth: window.screen.colorDepth,
            pixelDepth: window.screen.pixelDepth,
          },
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
        }
        this.collected.device = device
        this.collected.timestamps.deviceCollected = moment().toISOString()
      } catch (e) {
        this.collected.device = { error: e.message }
      }
    },
    async collectIP() {
      // 1) tenta serviço público (ipapi)
      try {
        const res = await axios.get('https://ipapi.co/json/')
        this.collected.ip = (res && res.data && res.data.ip) ? String(res.data.ip) : null
        this.collected.timestamps.ipCollected = moment().toISOString()
        return
      } catch { /* ignore errors here */ }

      // 2) fallback: seu backend /api/seguranca/whoami (usa axios configurado)
      try {
        // rota relativa; axios já tem baseURL configurada em axios.js
        const res = await axios.get('/seguranca/whoami')
        // aceita formatos: { ip: 'x.x.x.x' } ou 'x.x.x.x'
        let ip = null
        if (res && res.data) {
          if (typeof res.data === 'string') ip = res.data
          else if (res.data.ip) ip = res.data.ip
          else if (res.data.clientIp) ip = res.data.clientIp
        }
        this.collected.ip = ip ? String(ip) : null
        this.collected.timestamps.ipCollected = moment().toISOString()
      } catch { /* ignore errors here */ }
    },

    async submitSignature() {
      const documentoId = this.$route && this.$route.params ? this.$route.params.documentoId : null
      if (!documentoId) {
        this.$bvToast.toast('Documento inválido', { variant: 'danger' })
        return
      }

      // Validate name
      if (!this.form.nome || !this.form.nome.trim()) {
        this.$bvToast.toast('Nome é obrigatório', { variant: 'danger' })
        return
      }

      // Validate tipoInscricao / inscricao
      if (!this.form.tipoInscricao) {
        this.$bvToast.toast('Tipo de inscrição é obrigatório', { variant: 'danger' })
        return
      }
      if (!this.form.inscricao || !this.form.inscricao.trim()) {
        this.$bvToast.toast('Inscrição é obrigatória', { variant: 'danger' })
        return
      }
      const inscricaoDigits = String(this.form.inscricao).replace(/\D/g, '')
      if (this.form.tipoInscricao === 'CPF' && inscricaoDigits.length !== 11) {
        this.$bvToast.toast('CPF inválido', { variant: 'danger' })
        return
      }
      if (this.form.tipoInscricao === 'CNPJ' && inscricaoDigits.length !== 14) {
        this.$bvToast.toast('CNPJ inválido', { variant: 'danger' })
        return
      }

      // Validate date of birth and 18+
      if (!this.form.dataNascimento) {
        this.$bvToast.toast('Data de nascimento é obrigatória', { variant: 'danger' })
        return
      }
      const dob = moment(this.form.dataNascimento, 'YYYY-MM-DD')
      if (!dob.isValid() || moment().diff(dob, 'years') < 16) {
        this.$bvToast.toast('Assinante deve ter idade legal para assinar o documento', { variant: 'danger' })
        return
      }

      // Check if geolocation is available
      if (!this.collected.geolocation || this.collected.geolocation.error) {
        this.$bvToast.toast('Geolocalização é obrigatória para assinatura', { variant: 'danger' })
        return
      }

      this.collected.timestamps.signedRequested = moment().toISOString()
      const actionStarted = performance.now()

      const payload = {
        documentoId,
        token: this.token,
        signer: {
          nome: this.form.nome,
          tipoInscricao: this.form.tipoInscricao,
          inscricao: inscricaoDigits,
          dataNascimento: this.form.dataNascimento,
          email: this.assinanteInfo ? this.assinanteInfo.email : undefined,
        },
        device: this.collected.device,
        geolocation: this.collected.geolocation,
        // garante envio de string|null (não objeto de erro)
        ip: typeof this.collected.ip === 'string' ? this.collected.ip : null,
        timestamps: this.collected.timestamps,
      }

      try {
        await docuSign.enviarAssinatura(documentoId, payload)
        const actionEnded = performance.now()
        this.collected.timestamps.signedMs = Math.round(actionEnded - actionStarted)
        this.$bvToast.toast('Documento assinado com sucesso', { variant: 'success' })
        this.$refs.signModal.hide()
        this.$router.push({ name: 'portalproposta-consulta', params: { publicKey: this.chavePublicaDocumento } })
      } catch {
        this.$bvToast.toast('Erro ao assinar documento', { variant: 'danger' })
      }
    },
  },
}
</script>

<style scoped>
.assinador-documento {
  padding: 1rem;
}
.pdf-container {
  border: 1px solid #e6e6e6;
  border-radius: 8px;
  overflow: hidden;
}
.viewer-header {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #eee;
}
.assinante-info {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #eee;
  background: #f8f9fa;
}
.viewer {
  height: 70vh;
  overflow: auto;
}
.actions {
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.no-file {
  padding: 2rem;
  text-align: center;
  color: #666;
}
.loading-pdf {
  padding: 2rem;
  text-align: center;
  color: #666;
}

/* Mobile adjustments */
@media (max-width: 768px) {
  .assinador-documento {
    padding: 0.5rem;
  }
  .viewer-header {
    padding: 0.5rem;
    font-size: 1rem;
  }
  .viewer-header h4 {
    font-size: 1.2rem;
  }
  .assinante-info {
    padding: 0.5rem;
    font-size: 0.9rem;
  }
  .viewer {
    height: 60vh;
  }
  .actions {
    padding: 0.5rem;
  }
  .actions .btn {
    width: 100%;
    margin-top: 0.5rem;
  }
  .no-file,
  .loading-pdf {
    padding: 1rem;
    font-size: 0.9rem;
  }
}

/* iPhone specific */
@media (max-width: 480px) {
  .viewer {
    height: 50vh;
  }
  .assinador-documento {
    padding: 0.25rem;
  }
  .pdf-container {
    border-radius: 4px;
  }
}
</style>
